import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { v4 as uuidv4 } from "uuid";
import { NumericFormat as NumberFormat } from "react-number-format";

import ConditionalButton from "../../../layout/conditional-button";
import { canManageFeradelAdministration, canViewFeradelAdministration } from "../../../constants/permissions";
import { send as sendPageSetting } from "../../../actions/page";
import { sourceDestroy, sourceGet, sourceSave } from "../../../actions/products";
import { sourceList as listAreas } from "../../../actions/areas";
import { list as getCodes } from "../../../actions/code";
import { isGuid } from "../../../utils/string";
import CsLineIcons from "../../../cs-line-icons";

const emptyProduct = () => ({
    uuid: uuidv4(),
    areaUuid: null,
    category: "",
    default: true,
    name: "",
    caption: "",
    description: "",
    modifiers: "{}",
    price: 0,
    frequency: "M",
    visibility: 0,
    change: 0,
    limit: 0
});

const FREQUENCY_OPTIONS = [
    { value: "M", label: "Monthly" },
    { value: "A", label: "Annual" },
    { value: "P", label: "Perpetual" }
];

const CHAT_SLACK_CATEGORY = "Retail.Chat.Slack";

const chatSlackDefaults = () => ({
    users: 1,
    messages: 1000,
    reporting: false,
    scheduling: false,
    channels: 1,
    api: false,
    archive: 1,
    add: {
        user: 0,
        messages: 0
    }
});

const safeParseModifiers = (value) => {
    try {
        return typeof value === "string" ? JSON.parse(value || "{}") : (value || {});
    } catch (_err) {
        return {};
    }
};

const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true";
    return Boolean(value);
};

export const productEditorLabel = (product = {}, fallback = "Product") => {
    const caption = String(product.caption || "").trim();
    const name = String(product.name || "").trim();

    if (caption && name) return `${caption} [${name}]`;
    if (caption) return caption;
    if (name) return `[${name}]`;
    return fallback;
};

export class ProductSourceEditor extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            data: emptyProduct(),
            areas: [],
            categories: [],
            error: null,
            loading: true
        };
    }

    componentDidMount = () => {
        this.loadData();
    }

    componentDidUpdate = (oldProps) => {
        if (oldProps.match?.params?.uuid !== this.props.match?.params?.uuid) {
            this.loadData();
            return;
        }

        const oldPermissions = (oldProps.auth?.permissions || []).join("|");
        const newPermissions = (this.props.auth?.permissions || []).join("|");
        if (oldPermissions !== newPermissions && this.canViewProduct && this.state.loading) {
            this.loadData();
        }
    }

    get uuid() {
        return this.props.match?.params?.uuid;
    }

    get isNew() {
        return !isGuid(this.uuid);
    }

    get canViewProduct() {
        return canViewFeradelAdministration(this.props.auth?.permissions);
    }

    get canManageProduct() {
        return canManageFeradelAdministration(this.props.auth?.permissions);
    }

    get categoryOptions() {
        return (this.state.categories || []).map(item => ({
            value: item.value,
            label: item.description || item.value
        }));
    }

    get areaOptions() {
        return (this.state.areas || []).map(item => ({
            value: item.uuid,
            label: `${item.name || item.abbreviation || item.uuid}${item.abbreviation ? ` (${item.abbreviation})` : ""}`
        }));
    }

    get selectedCategory() {
        return this.categoryOptions.find(option => option.value === this.state.data.category) || null;
    }

    get selectedArea() {
        return this.areaOptions.find(option => option.value === this.state.data.areaUuid) || null;
    }

    get selectedFrequency() {
        return FREQUENCY_OPTIONS.find(option => option.value === this.state.data.frequency) || FREQUENCY_OPTIONS[0];
    }

    get isChatSlackProduct() {
        return this.state.data.category === CHAT_SLACK_CATEGORY;
    }

    get modifiers() {
        return safeParseModifiers(this.state.data.modifiers);
    }

    get chatSlackModifiers() {
        const current = this.modifiers;
        return {
            ...chatSlackDefaults(),
            ...current,
            add: {
                ...chatSlackDefaults().add,
                ...(current.add || {})
            },
            reporting: toBoolean(current.reporting),
            scheduling: toBoolean(current.scheduling),
            api: toBoolean(current.api)
        };
    }

    updatePageIdentity = (data) => {
        const label = productEditorLabel(data, this.isNew ? "New Product" : "Product");
        this.props.sendPageSetting({
            context: "settings-product-editor",
            title: label,
            breadcrumb: label
        });
    }

    loadData = async () => {
        if (!this.canViewProduct) return;

        this.setState({ loading: true });
        const [categories, areaResult] = await Promise.all([
            this.props.getCodes("PR"),
            this.props.listAreas({ page: 0, size: 1000, order: "name", direction: "ASC", raw: 1, search: "" })
        ]);
        let data = emptyProduct();

        if (!this.isNew) {
            const loaded = await this.props.sourceGet({ uuid: this.uuid });
            if (loaded?.uuid) {
                data = {
                    ...data,
                    ...loaded,
                    areaUuid: loaded.areaUuid || null,
                    modifiers: typeof loaded.modifiers === "string" ? loaded.modifiers : JSON.stringify(loaded.modifiers || {}, null, 2)
                };
            }
        }

        this.updatePageIdentity(data);
        this.setState({
            areas: areaResult?.rows || [],
            categories: categories || [],
            data,
            loading: false
        });
    }

    onChange = (e) => {
        if (!this.canManageProduct) return;

        const { name, value, type, checked } = e.target;
        const data = {
            ...this.state.data,
            [name]: type === "checkbox" ? checked : value
        };
        this.setState({
            data,
            error: null
        });

        if (name === "caption" || name === "name") {
            this.updatePageIdentity(data);
        }
    }

    onNumberChange = (name, value) => {
        this.onChange({ target: { name, value: value ?? 0 } });
    }

    onSelectChange = (name, option) => {
        this.onChange({ target: { name, value: option?.value || null } });
    }

    onCategoryChange = (option) => {
        if (!this.canManageProduct) return;

        const category = option?.value || "";
        this.setState(prev => {
            const currentModifiers = String(prev.data.modifiers || "").trim();
            const modifiers = category === CHAT_SLACK_CATEGORY && (!currentModifiers || currentModifiers === "{}")
                ? JSON.stringify(chatSlackDefaults(), null, 2)
                : prev.data.modifiers;

            return {
                data: {
                    ...prev.data,
                    category,
                    modifiers
                },
                error: null
            };
        });
    }

    updateModifiers = (patch) => {
        if (!this.canManageProduct) return;

        this.setState(prev => {
            const modifiers = {
                ...safeParseModifiers(prev.data.modifiers),
                ...patch
            };

            return {
                data: {
                    ...prev.data,
                    modifiers: JSON.stringify(modifiers, null, 2)
                },
                error: null
            };
        });
    }

    onModifierNumberChange = (name, value) => {
        this.updateModifiers({ [name]: value ?? 0 });
    }

    onModifierBooleanChange = (name, value) => {
        this.updateModifiers({ [name]: Boolean(value) });
    }

    onModifierAddNumberChange = (name, value) => {
        const modifiers = this.modifiers;
        this.updateModifiers({
            add: {
                ...(modifiers.add || {}),
                [name]: value ?? 0
            }
        });
    }

    onLimitInfiniteChange = (name, value) => {
        const patch = { [name]: value ? -1 : 1 };
        if (name === "users" && value) {
            patch.add = {
                ...(this.modifiers.add || {}),
                user: 0
            };
        }
        if (name === "messages" && value) {
            patch.add = {
                ...(this.modifiers.add || {}),
                messages: 0
            };
        }
        this.updateModifiers(patch);
    }

    onLimitChange = (name, value) => {
        const minimum = name === "archive" ? 0 : 1;
        this.updateModifiers({ [name]: Math.max(minimum, Number(value ?? minimum)) });
    }

    validate = () => {
        if (!String(this.state.data.category || "").trim()) return "Category is required.";
        if (!String(this.state.data.name || "").trim()) return "Name is required.";
        if (!String(this.state.data.caption || "").trim()) return "Caption is required.";

        try {
            const modifiers = JSON.parse(this.state.data.modifiers || "{}");
            if (this.isChatSlackProduct) {
                if (["users", "messages", "channels"].some(name => Number(modifiers[name] || 0) === 0)) {
                    return "Users, messages, and channels must be infinite or greater than zero.";
                }

                if (Number(modifiers.archive) < -1) {
                    return "Archive must be none, infinite, or greater than zero.";
                }
            }
        } catch (_err) {
            return "Modifiers must be valid JSON.";
        }

        return null;
    }

    onSaveClick = async () => {
        if (!this.canManageProduct) return;

        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        const saved = await this.props.sourceSave({
            ...this.state.data,
            areaUuid: isGuid(this.state.data.areaUuid) ? this.state.data.areaUuid : null,
            modifiers: JSON.stringify(JSON.parse(this.state.data.modifiers || "{}"))
        });

        if (saved?.success === false || !saved?.uuid) {
            this.setState({ error: saved?.error || "The product could not be saved." });
            return;
        }

        window.location.href = `/dashboard/settings/products/${saved.uuid}`;
    }

    onDeleteClick = async () => {
        if (!this.canManageProduct || this.isNew) return;

        await this.props.sourceDestroy({ uuid: this.state.data.uuid });
        window.location.href = "/dashboard/settings/products";
    }

    render() {
        if (!this.canViewProduct) {
            return <Col xs="12">Product settings not available.</Col>;
        }

        if (this.state.loading) {
            return <Col xs="12">Loading...</Col>;
        }

        return (
            <>
                <Col xs="12" className="licensee-editor-toolbar">
                    <ConditionalButton className="licensee-save-button" enabledVariant="primary" tooltip="Save product" display={this.canManageProduct} enabled={this.canManageProduct} onEnabledClick={this.onSaveClick} icon="check-square">Save Changes</ConditionalButton>
                    <ConditionalButton className="licensee-delete-button" enabledVariant="outline-danger" tooltip="Delete product" display={this.canManageProduct && !this.isNew} enabled={this.canManageProduct && !this.isNew} onEnabledClick={this.onDeleteClick} icon="close-circle">Delete</ConditionalButton>
                </Col>
                {this.state.error ? (
                    <Col xs="12">
                        <div className="alert alert-danger">{this.state.error}</div>
                    </Col>
                ) : null}
                <Col xs="12" className="licensee-editor">
                    <section className="licensee-editor-card">
                        <header className="licensee-editor-card-header">
                            <span><CsLineIcons icon="grid-2" size="21" /></span>
                            <h2>Product Definition</h2>
                        </header>
                    <Row className="g-3">
                        <Col xs="12" md="6">
                            <div className="mb-3 licensee-field">
                                <Form.Label>NAME</Form.Label>
                                <Form.Control type="text" name="name" disabled={!this.canManageProduct} value={this.state.data.name || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div className="mb-3 licensee-field">
                                <Form.Label>CAPTION</Form.Label>
                                <Form.Control type="text" name="caption" disabled={!this.canManageProduct} value={this.state.data.caption || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div className="mb-3 licensee-field">
                                <Form.Label>CATEGORY</Form.Label>
                                <Select
                                    inputId="productCategory"
                                    name="category"
                                    value={this.selectedCategory}
                                    onChange={this.onCategoryChange}
                                    classNamePrefix="react-select"
                                    placeholder="Category"
                                    options={this.categoryOptions}
                                    isDisabled={!this.canManageProduct}
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>FREQUENCY</Form.Label>
                                <Select
                                    inputId="productFrequency"
                                    name="frequency"
                                    value={this.selectedFrequency}
                                    onChange={(option) => this.onSelectChange("frequency", option)}
                                    classNamePrefix="react-select"
                                    placeholder="Frequency"
                                    options={FREQUENCY_OPTIONS}
                                    isDisabled={!this.canManageProduct}
                                />
                            </div>
                        </Col>
                        <Col xs="12" md="3">
                            <div className="mb-3 licensee-field">
                                <Form.Label>PRICE</Form.Label>
                                <NumberFormat
                                    displayType="input"
                                    type="text"
                                    name="price"
                                    disabled={!this.canManageProduct}
                                    className="form-control text-end"
                                    prefix="$"
                                    thousandSeparator={false}
                                    decimalScale={2}
                                    value={this.state.data.price ?? 0}
                                    onValueChange={(e) => this.onNumberChange("price", e.floatValue)}
                                />
                            </div>
                        </Col>
                        <Col xs="12">
                            <div className="mb-3 licensee-field">
                                <Form.Label>DESCRIPTION</Form.Label>
                                <Form.Control type="text" name="description" disabled={!this.canManageProduct} value={this.state.data.description || ""} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="6">
                            <div className="mb-3 licensee-field">
                                <Form.Label>AREA</Form.Label>
                                <Select
                                    inputId="productAreaUuid"
                                    name="areaUuid"
                                    value={this.selectedArea}
                                    onChange={(option) => this.onSelectChange("areaUuid", option)}
                                    classNamePrefix="react-select"
                                    placeholder="Area"
                                    options={this.areaOptions}
                                    isDisabled={!this.canManageProduct}
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col xs="12" md="2">
                            <div className="mb-3 licensee-field">
                                <Form.Label>VISIBILITY</Form.Label>
                                <Form.Control type="number" name="visibility" disabled={!this.canManageProduct} value={this.state.data.visibility ?? 0} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="2">
                            <div className="mb-3 licensee-field">
                                <Form.Label>CHANGE</Form.Label>
                                <Form.Control type="number" name="change" disabled={!this.canManageProduct} value={this.state.data.change ?? 0} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="2">
                            <div className="mb-3 licensee-field">
                                <Form.Label>LIMIT</Form.Label>
                                <Form.Control type="number" name="limit" disabled={!this.canManageProduct} value={this.state.data.limit ?? 0} onChange={this.onChange} />
                            </div>
                        </Col>
                        <Col xs="12" md="2">
                            <Form.Check type="checkbox" name="default" disabled={!this.canManageProduct} checked={Boolean(this.state.data.default)} onChange={this.onChange} label="Default" />
                        </Col>
                        {this.isChatSlackProduct ? this.renderChatSlackModifiers() : this.renderJsonModifiers()}
                    </Row>
                    </section>
                </Col>
            </>
        );
    }

    renderChatSlackModifiers = () => {
        const modifiers = this.chatSlackModifiers;
        const infiniteUsers = Number(modifiers.users) === -1;
        const infiniteMessages = Number(modifiers.messages) === -1;
        const infiniteChannels = Number(modifiers.channels) === -1;
        const infiniteArchive = Number(modifiers.archive) === -1;

        return (
            <>
                <Col xs="12">
                    <h2 className="small-title mt-3">FeraChat Properties</h2>
                </Col>
                {this.renderLimitField("users", "USERS", modifiers.users, infiniteUsers)}
                {this.renderLimitField("messages", "MESSAGES", modifiers.messages, infiniteMessages)}
                {this.renderLimitField("channels", "CHANNELS", modifiers.channels, infiniteChannels)}
                {this.renderLimitField("archive", "ARCHIVE", modifiers.archive, infiniteArchive)}
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={Boolean(modifiers.reporting)} onChange={(e) => this.onModifierBooleanChange("reporting", e.target.checked)} label="Reporting" />
                </Col>
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={Boolean(modifiers.scheduling)} onChange={(e) => this.onModifierBooleanChange("scheduling", e.target.checked)} label="Advanced Scheduling" />
                </Col>
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={Boolean(modifiers.api)} onChange={(e) => this.onModifierBooleanChange("api", e.target.checked)} label="API Access" />
                </Col>
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={infiniteUsers} onChange={(e) => this.onLimitInfiniteChange("users", e.target.checked)} label="Infinite Users" />
                </Col>
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={infiniteMessages} onChange={(e) => this.onLimitInfiniteChange("messages", e.target.checked)} label="Infinite Messages" />
                </Col>
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={infiniteChannels} onChange={(e) => this.onLimitInfiniteChange("channels", e.target.checked)} label="Infinite Channels" />
                </Col>
                <Col xs="12" md="4">
                    <Form.Check type="checkbox" disabled={!this.canManageProduct} checked={infiniteArchive} onChange={(e) => this.onLimitInfiniteChange("archive", e.target.checked)} label="Infinite Archive" />
                </Col>
                {(!infiniteUsers || !infiniteMessages) ? (
                    <Col xs="12" className="pt-3">
                        <h2 className="small-title mb-3">Additional Costs</h2>
                    </Col>
                ) : null}
                {!infiniteUsers ? this.renderAdditionalFeeField("user", "ADDITIONAL USER", modifiers.add?.user) : null}
                {!infiniteMessages ? this.renderAdditionalFeeField("messages", "ADDITIONAL MESSAGES", modifiers.add?.messages) : null}
            </>
        );
    }

    renderLimitField = (name, label, value, infinite) => (
        <Col xs="12" md="3">
            <div className="mb-3 licensee-field">
                <Form.Label>{label}</Form.Label>
                {infinite ? (
                    <Form.Control type="text" disabled className="text-end" value="Infinite" />
                ) : (
                    <NumberFormat
                        displayType="input"
                        type="text"
                        disabled={!this.canManageProduct}
                        className="form-control text-end"
                        thousandSeparator={false}
                        decimalScale={0}
                        allowNegative={false}
                        value={Math.max(name === "archive" ? 0 : 1, Number(value ?? (name === "archive" ? 0 : 1)))}
                        onValueChange={(e) => this.onLimitChange(name, e.floatValue)}
                        isAllowed={({ floatValue }) => floatValue == null || floatValue >= (name === "archive" ? 0 : 1)}
                    />
                )}
            </div>
        </Col>
    )

    renderAdditionalFeeField = (name, label, value) => (
        <Col xs="12" sm="6" lg="3">
            <div className="mb-3 licensee-field">
                <Form.Label>{label}</Form.Label>
                <NumberFormat displayType="input" type="text" disabled={!this.canManageProduct} className="form-control text-end" prefix="$" thousandSeparator={false} decimalScale={2} value={value ?? 0} onValueChange={(e) => this.onModifierAddNumberChange(name, e.floatValue)} />
            </div>
        </Col>
    )

    renderJsonModifiers = () => (
        <Col xs="12">
            <div className="mb-3 licensee-field">
                <Form.Label>MODIFIERS</Form.Label>
                <Form.Control as="textarea" rows={8} name="modifiers" disabled={!this.canManageProduct} value={this.state.data.modifiers || "{}"} onChange={this.onChange} />
            </div>
        </Col>
    )
}

ProductSourceEditor.propTypes = {
    auth: PropTypes.object.isRequired,
    sendPageSetting: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps, { getCodes, listAreas, sendPageSetting, sourceDestroy, sourceGet, sourceSave })(ProductSourceEditor);
